import type { UserProfile } from '../types';
import type { GetControlCentreAvailabilityResponse } from '../services/functions';

export type ControlCentreFrontendReason =
  | 'enabled'
  | 'build_disabled'
  | 'unauthenticated'
  | 'backend_disabled'
  | 'kill_switch_active'
  | 'not_allowlisted'
  | 'user_disabled'
  | 'tier_ineligible'
  | 'backend_check_failed';

export interface ControlCentreFrontendGateState {
  available: boolean;
  reason: ControlCentreFrontendReason;
  gates: {
    buildEnabled: boolean;
    backendEnabled: boolean;
    killSwitchActive: boolean;
    allowlisted: boolean;
    userEnabled: boolean;
    tierEligible: boolean;
  };
  tier: {
    userTier: string | null;
    requiredTier: string | null;
  };
}

const CONTROL_CENTRE_BUILD_ENABLED = import.meta.env.VITE_ENABLE_CONTROL_CENTRE === 'true';

export function isControlCentreBuildEnabled(): boolean {
  return CONTROL_CENTRE_BUILD_ENABLED;
}

export function isControlCentreUserEnabled(profile: UserProfile | null | undefined): boolean {
  return Boolean(profile?.features?.controlCentre === true);
}

export function buildDisabledState(profile: UserProfile | null | undefined): ControlCentreFrontendGateState {
  const userEnabled = isControlCentreUserEnabled(profile);
  return {
    available: false,
    reason: 'build_disabled',
    gates: {
      buildEnabled: false,
      backendEnabled: false,
      killSwitchActive: false,
      allowlisted: false,
      userEnabled,
      tierEligible: false,
    },
    tier: { userTier: null, requiredTier: null },
  };
}

export function unauthenticatedState(): ControlCentreFrontendGateState {
  return {
    available: false,
    reason: 'unauthenticated',
    gates: {
      buildEnabled: CONTROL_CENTRE_BUILD_ENABLED,
      backendEnabled: false,
      killSwitchActive: false,
      allowlisted: false,
      userEnabled: false,
      tierEligible: false,
    },
    tier: { userTier: null, requiredTier: null },
  };
}

export function fromBackendGate(
  backend: GetControlCentreAvailabilityResponse
): ControlCentreFrontendGateState {
  const gates = backend && typeof backend === 'object' && 'gates' in backend && backend.gates
    ? backend.gates
    : undefined;
  const tier = backend && typeof backend === 'object' && 'tier' in backend && backend.tier
    ? backend.tier
    : undefined;
  const reason = backend?.reason ?? 'backend_check_failed';

  return {
    available: Boolean(backend?.available),
    reason,
    gates: {
      buildEnabled: CONTROL_CENTRE_BUILD_ENABLED,
      backendEnabled: Boolean(gates?.backendEnabled),
      killSwitchActive: Boolean(gates?.killSwitchActive),
      allowlisted: Boolean(gates?.allowlisted),
      userEnabled: Boolean(gates?.userEnabled),
      tierEligible: typeof gates?.tierEligible === 'boolean' ? gates.tierEligible : true,
    },
    tier: {
      userTier: typeof tier?.userTier === 'string' ? tier.userTier : null,
      requiredTier: typeof tier?.requiredTier === 'string' ? tier.requiredTier : null,
    },
  };
}

export function backendCheckFailedState(profile: UserProfile | null | undefined): ControlCentreFrontendGateState {
  return {
    available: false,
    reason: 'backend_check_failed',
    gates: {
      buildEnabled: CONTROL_CENTRE_BUILD_ENABLED,
      backendEnabled: false,
      killSwitchActive: false,
      allowlisted: false,
      userEnabled: isControlCentreUserEnabled(profile),
      tierEligible: false,
    },
    tier: { userTier: null, requiredTier: null },
  };
}
